import { useState, useEffect } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { SkillPicker } from "./SkillPicker";
import { PvpButton } from "./PvpButton";
import { BehaviorGuideButton } from "./BehaviorGuideButton";
import { MoodBadge } from "./Mood";
import { MoodHistory } from "./MoodHistory";

export const EmployeeEditSheet = ({ employee, strategies, canEdit, onClose, onSaved }) => {
  const [form, setForm] = useState(null);
  const [skills, setSkills] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pvps, setPvps] = useState([]);
  const [mood, setMood] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (employee) api.get(`/employees/${employee.id}/pvps`).then(({ data }) => setPvps(data));
    if (employee) api.get(`/organizations/${employee.organization_id}/moods`).then(({ data }) => setMood(data[employee.id] || null));
  }, [employee]);

  useEffect(() => {
    if (!employee) return;
    setForm({ name: employee.name, role: employee.role || "", email: employee.email || "", phone: employee.phone || "", notes: employee.notes || "", brain_type: employee.brain_type || 0 });
    setSkills(employee.skill_ids || []);
  }, [employee]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Naam is verplicht");
    setSaving(true);
    try {
      await api.patch(`/employees/${employee.id}`, form);
      const { data } = await api.put(`/employees/${employee.id}/skills`, { behavior_ids: skills });
      onSaved(data);
      toast.success("Medewerker opgeslagen");
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={!!employee} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto rounded-none" data-testid="employee-edit-sheet">
        <SheetHeader>
          <SheetTitle className="font-grotesk text-2xl">{employee?.name}</SheetTitle>
          <SheetDescription>{canEdit ? "Gegevens en vaardigheden bewerken" : "Alleen bekijken"}</SheetDescription>
        </SheetHeader>
        {form && employee && (
          <div className="mt-6 space-y-4">
            {mood && (
              <div className="border border-zinc-300 px-3 py-2 space-y-2" data-testid="employee-mood">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-widest text-zinc-500">Hoe gaat het</span>
                  <MoodBadge mood={mood} testid="employee-mood-badge" />
                </div>
                <button type="button" onClick={() => setShowHistory((v) => !v)} className="text-xs underline underline-offset-4 text-zinc-600 hover:text-zinc-900" data-testid="mood-history-toggle">
                  {showHistory ? "Verberg verloop" : "Toon verloop"}
                </button>
                {showHistory && <MoodHistory employeeId={employee.id} />}
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Breintype</Label>
              <div className="flex flex-wrap gap-1" data-testid="employee-brain-type">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                  <button key={n} type="button" disabled={!canEdit} onClick={() => setForm({ ...form, brain_type: form.brain_type === n ? 0 : n })}
                    className={`h-8 w-9 border text-sm font-mono transition-colors disabled:cursor-not-allowed ${form.brain_type === n ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 hover:border-zinc-900"}`}
                    data-testid={`employee-brain-type-${n}`}>#{n}</button>
                ))}
              </div>
              {!form.brain_type && <p className="text-xs text-zinc-400">Nog geen breintype vastgelegd.</p>}
            </div>
            {[["name", "Naam"], ["role", "Rol / functie"], ["email", "E-mail"], ["phone", "Telefoonnummer"]].map(([k, l]) => (
              <div key={k} className="space-y-1">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">{l}</Label>
                <Input value={form[k]} disabled={!canEdit} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="rounded-none border-zinc-400" data-testid={`employee-edit-${k}-input`} />
              </div>
            ))}
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Notities</Label>
              <Textarea value={form.notes} disabled={!canEdit} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-none border-zinc-400" rows={3} data-testid="employee-edit-notes-input" />
            </div>

            <div className="pt-4 border-t border-zinc-900">
              <SkillPicker strategies={strategies} value={skills} onChange={setSkills} disabled={!canEdit} renderExtra={(b, s) => (
                <span className="flex items-center gap-1 shrink-0">
                  <BehaviorGuideButton behavior={b} strategyName={s.name} />
                  {(canEdit || pvps.some((p) => p.behavior_id === b.id)) && <PvpButton employee={employee} behavior={b} saved={canEdit && (employee.skill_ids || []).includes(b.id)} pvp={pvps.find((p) => p.behavior_id === b.id)} />}
                </span>
              )} />
            </div>
            {canEdit && (
              <Button className="w-full rounded-none bg-zinc-900 hover:bg-zinc-700" onClick={save} disabled={saving} data-testid="employee-edit-save-button">
                {saving ? "Opslaan…" : "Opslaan"}
              </Button>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
